import React from 'react';
import {
  CL, Easing, T, env, rise, kf,
  DISPLAY, BODY, MONO, SOFT, INK, RED, GREEN, DIM, MUT, ACCENT, GRAD,
  TimeContext, useTime,
  Frame, Radar as RadarBase, PulseArcs, Rings, Cane, SideCane, Silhouette,
} from './shared';

/* ClearWay Explainer — mobile 9:16 (1080x1920) recomposition.
 *
 * Ported from the Claude Design handoff (`ClearWay Explainer Mobile.dc.html`
 * / clearway-scene-mobile.jsx): the same 56s story and product renders as
 * the desktop stage, recomposed for a portrait canvas — captions sit above
 * the action, side callouts become centred stacked labels (MCallout), and
 * scene geometry is condensed horizontally. Driven by the same plain `time`
 * prop; the parent picks this stage for portrait viewports.
 */

const W = 1080, H = 1920;
const E = Easing;

const Radar = (props) => <RadarBase w={W} h={H} cx={540} cy={960} maxR={560} base={44} {...props} />;

// ── Stage-format atoms ────────────────────────────────────────────────
function Cap({ t0, t1, text, size = 64, y = 200, maxW = 940, color, useGrad = true, weight = 700, track = '-0.03em' }) {
  const t = useTime();
  const o = env(t, t0, 0.7, t1, 0.5);
  if (o <= 0) return null;
  const dy = rise(t, t0, 0.9, 30);
  return (
    <div style={{
      position: 'absolute', left: '50%', top: y, transform: `translate(-50%,${dy}px)`,
      width: maxW, textAlign: 'center', fontFamily: DISPLAY, fontWeight: weight,
      fontSize: size, letterSpacing: track, lineHeight: 1.08, opacity: o,
      ...(useGrad && !color ? GRAD : { color: color || INK }),
    }}>{text}</div>
  );
}

function Pill({ t0, t1, y, text }) {
  const t = useTime();
  const o = env(t, t0, 0.6, t1, 0.4);
  if (o <= 0) return null;
  return (
    <div style={{
      position: 'absolute', left: '50%', top: y,
      transform: `translateX(-50%) translateY(${rise(t, t0, 0.8, 18)}px)`, opacity: o,
      display: 'inline-flex', alignItems: 'center', gap: 10, padding: '9px 20px',
      borderRadius: 9999, border: '1px solid rgba(59,130,246,0.25)',
      background: 'rgba(59,130,246,0.07)', color: SOFT, fontFamily: BODY,
      fontSize: 16, fontWeight: 600, letterSpacing: '0.22em', textTransform: 'uppercase',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: SOFT, boxShadow: `0 0 10px ${SOFT}` }} />
      {text}
    </div>
  );
}

// Centred label with leader dot — replaces side callouts on the narrow canvas
function MCallout({ lt, at, endLt, y, title, sub }) {
  const o = env(lt, at, 0.6, endLt, 0.4);
  if (o <= 0) return null;
  return (
    <div style={{
      position: 'absolute', left: '50%', top: y, transform: `translateX(-50%) translateY(${rise(lt, at, 0.8, 20)}px)`,
      opacity: o, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, width: 700, textAlign: 'center',
    }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: SOFT, boxShadow: `0 0 12px ${SOFT}` }} />
      <span style={{ width: 1, height: 26, background: 'rgba(96,165,250,0.5)' }} />
      <div style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 30, color: INK, letterSpacing: '-0.015em' }}>{title}</div>
      <div style={{ fontFamily: BODY, fontSize: 21, color: DIM }}>{sub}</div>
    </div>
  );
}

function Chip({ x, y, o = 1, tone = 'accent', label, value }) {
  const colors = {
    accent: { b: 'rgba(59,130,246,0.3)', bg: 'rgba(59,130,246,0.08)', c: SOFT },
    danger: { b: 'rgba(248,113,113,0.4)', bg: 'rgba(239,68,68,0.10)', c: RED },
    ok: { b: 'rgba(52,211,153,0.35)', bg: 'rgba(52,211,153,0.08)', c: GREEN },
  }[tone];
  return (
    <div style={{
      position: 'absolute', left: x, top: y, transform: 'translateX(-50%)', opacity: o,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
      padding: '12px 24px', borderRadius: 16, border: `1px solid ${colors.b}`,
      background: colors.bg, backdropFilter: 'blur(12px)',
    }}>
      <span style={{ fontFamily: BODY, fontSize: 14, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: colors.c }}>{label}</span>
      {value && <span style={{ fontFamily: MONO, fontSize: 36, fontWeight: 600, color: INK, fontVariantNumeric: 'tabular-nums' }}>{value}</span>}
    </div>
  );
}

// ── Scene 1 · Intro ─────────────────────────────────────────────────
function Intro() {
  const t = useTime();
  if (t > T.problem + 0.2) return null;
  return (
    <Frame t0={0} t1={T.problem} zoomFrom={1.05} zoomTo={1} fadeIn={0.9}>
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(560px 560px at 50% 50%, rgba(59,130,246,0.07), transparent 70%)' }} />
      <Radar t={t} alpha={0.3} />
      <Pill t0={0.7} t1={5.9} y={780} text="Introducing" />
      <Cap t0={1.3} t1={6.0} size={128} y={856} text="ClearWay" />
      <Cap t0={2.4} t1={6.0} size={30} y={1030} useGrad={false} color={MUT} weight={500} track="-0.01em" text="The smart mobility stick." />
    </Frame>
  );
}

// ── Scene 2 · The problem ──────────────────────────────────────────
function FloorGrid({ t, opacity = 1 }) {
  const shift = (t * 60) % 90;
  return (
    <div style={{
      position: 'absolute', left: -260, right: -260, top: 1020, height: 900,
      transform: 'perspective(680px) rotateX(58deg)', transformOrigin: '50% 0%',
      backgroundImage: 'linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(0deg, rgba(255,255,255,0.06) 1px, transparent 1px)',
      backgroundSize: '90px 90px', backgroundPosition: `0px ${shift}px`, opacity,
      maskImage: 'linear-gradient(180deg, transparent 0%, #000 20%, #000 72%, transparent 100%)',
      WebkitMaskImage: 'linear-gradient(180deg, transparent 0%, #000 20%, #000 72%, transparent 100%)',
    }} />
  );
}

function Problem() {
  const t = useTime();
  if (t < T.problem - 0.3 || t > T.reveal + 0.2) return null;
  const lt = t - T.problem;
  return (
    <Frame t0={T.problem} t1={T.reveal} zoomFrom={1} zoomTo={1.09} origin="50% 70%">
      <FloorGrid t={lt} opacity={0.75} />
      <Silhouette lt={lt} delay={0.9} x={110} y={1010} w={190} h={310} />
      <Silhouette lt={lt} delay={1.7} x={740} y={980} w={220} h={350} />
      <Silhouette lt={lt} delay={2.7} x={410} y={1150} w={280} h={190} />
      <Cap t0={T.problem + 0.8} t1={T.problem + 4.7} size={68} y={330} text="Every step is a guess." />
      <Cap t0={T.problem + 5.1} t1={T.reveal - 0.4} size={62} y={300}
        text={<span>Obstacles you can't see.<br />Drops you can't predict.</span>} />
    </Frame>
  );
}

// ── Scene 3 · Reveal ────────────────────────────────────────────────
function Reveal() {
  const t = useTime();
  if (t < T.reveal - 0.3 || t > T.forward + 0.2) return null;
  const lt = t - T.reveal;
  const caneY = 2000 - E.easeOutQuart(CL(lt / 1.8, 0, 1)) * 1420;
  const sweep = CL((lt - 1.6) / 1.5, 0, 1);
  const glow = env(t, T.reveal + 1.4, 1.6, T.forward, 0.8);
  const endLt = T.forward - T.reveal - 0.4;
  return (
    <Frame t0={T.reveal} t1={T.forward} zoomFrom={1} zoomTo={1.07} origin="50% 55%">
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(540px 760px at 50% 56%, rgba(59,130,246,0.13), transparent 70%)', opacity: glow }} />
      <Radar cx={540} cy={1000} t={lt} alpha={0.13} maxR={470} dot={false} />
      <div style={{ position: 'absolute', left: 260, top: 1400, width: 560, height: 56, borderRadius: '50%', background: 'radial-gradient(closest-side, rgba(59,130,246,0.10), transparent)', opacity: glow }} />
      <div style={{ position: 'absolute', left: 460, top: caneY }}>
        <Cane h={800} glow={0.65 * glow} sweep={sweep} />
      </div>
      <Cap t0={T.reveal + 1.1} t1={T.reveal + 4.3} size={80} y={180} text="Meet ClearWay." />
      <MCallout lt={lt} at={4.6} endLt={endLt} y={1500} title="Dual smart sensors" sub="Forward + downward scanning" />
      <MCallout lt={lt} at={6.4} endLt={endLt} y={1700} title="Feather-light shaft · High-visibility marker" sub="Folds to fit in a bag" />
    </Frame>
  );
}

// ── Scene 4 · Forward detection (sensors) ────────────────────────────
function Forward() {
  const t = useTime();
  if (t < T.forward - 0.3 || t > T.down + 0.2) return null;
  const lt = t - T.forward;
  const floorY = 1400;
  const caneX = kf([1, 6.2, 7.0], [30, 470, 478], E.easeInOutQuad)(lt);
  const alerted = lt >= 6.2;
  const emit = { x: caneX + 64, y: floorY - 144 };
  const obsL = 810;
  const distM = Math.max((obsL - emit.x) / 300, 0);
  const flash = 0.3 + Math.max(0, Math.sin(lt * 5.5)) * 0.35;
  return (
    <Frame t0={T.forward} t1={T.down} zoomFrom={1.04} zoomTo={1.1} origin="55% 62%">
      <div style={{ position: 'absolute', left: 0, right: 0, top: floorY, height: 2, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.16) 20%, rgba(255,255,255,0.16) 80%, transparent)' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, top: floorY + 2, height: 300, background: 'linear-gradient(180deg, rgba(255,255,255,0.035), transparent 70%)' }} />
      {/* obstacle */}
      <div style={{
        position: 'absolute', left: obsL, top: floorY - 250, width: 140, height: 250, borderRadius: 14,
        background: 'linear-gradient(180deg, #1d1e22, #131418)',
        border: '1px solid rgba(255,255,255,0.09)',
        boxShadow: `0 30px 60px rgba(0,0,0,0.55)${lt > 2.4 ? `, -18px 0 44px rgba(59,130,246,${flash * 0.35})` : ''}`,
      }} />
      {/* echo shimmer on obstacle face */}
      {lt > 2.4 && !alerted && (
        <div style={{ position: 'absolute', left: obsL - 2, top: floorY - 250, width: 4, height: 250, borderRadius: 3, background: SOFT, opacity: flash * 0.7, filter: 'blur(1px)' }} />
      )}
      <SideCane x={caneX} y={floorY} t={lt} walking={!alerted} />
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ position: 'absolute', left: 0, top: 0 }}>
        <PulseArcs cx={emit.x} cy={emit.y} t={lt} startT={1.6} dir={-4} spread={56}
          maxR={Math.min(420, obsL - emit.x + 30)} color={alerted ? RED : ACCENT} alpha={alerted ? 0.9 : 0.7} period={alerted ? 0.65 : 1.05} />
        {/* alert rings on the grip (SideCane geometry: grip ≈ (caneX-138, floorY-608)) */}
        {alerted && <Rings cx={caneX - 138} cy={792} t={lt} startT={6.25} color={SOFT} maxR={130} />}
      </svg>
      {/* distance readout */}
      {lt > 2.2 && (
        <Chip x={540} y={880} o={env(lt, 2.2, 0.6, T.down - T.forward - 0.4, 0.4)}
          tone={alerted ? 'danger' : 'accent'}
          label={alerted ? 'Obstacle — alert' : 'Scanning ahead'}
          value={distM.toFixed(2) + ' m'} />
      )}
      <Cap t0={T.forward + 0.7} t1={T.forward + 4.6} size={64} y={220} text="Sees what you can't." />
      <Cap t0={T.forward + 6.7} t1={T.down - 0.4} size={62} y={220} text="Alerts you in time to stop." />
    </Frame>
  );
}

// ── Scene 5 · Downward scanning ─────────────────────────────────────
function Downward() {
  const t = useTime();
  if (t < T.down - 0.3 || t > T.alert + 0.2) return null;
  const lt = t - T.down;
  const upperY = 1380, lowerY = 1540, edgeX = 750;
  const caneX = kf([0.8, 5.2, 6.0], [40, 600, 610], E.easeInOutQuad)(lt);
  const emit = { x: caneX + 64, y: upperY - 144 };
  const dirRad = 38 * Math.PI / 180;
  const scanX = emit.x + (upperY - emit.y) / Math.tan(dirRad);
  const overEdge = scanX > edgeX;
  const alerted = overEdge && lt > 1.5;
  const hit = alerted
    ? { x: emit.x + (lowerY - emit.y) / Math.tan(dirRad), y: lowerY }
    : { x: scanX, y: upperY };
  const beamOn = lt > 1.4;
  return (
    <Frame t0={T.down} t1={T.alert} zoomFrom={1.03} zoomTo={1.1} origin="55% 66%">
      {/* two-level floor */}
      <div style={{ position: 'absolute', left: 0, width: edgeX, top: upperY, height: 2, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.16) 15%, rgba(255,255,255,0.18))' }} />
      <div style={{ position: 'absolute', left: edgeX, top: upperY, width: 2, height: lowerY - upperY, background: 'rgba(255,255,255,0.16)' }} />
      <div style={{ position: 'absolute', left: edgeX, right: 0, top: lowerY, height: 2, background: 'rgba(255,255,255,0.14)' }} />
      <div style={{ position: 'absolute', left: 0, width: edgeX, top: upperY + 2, height: 158, background: 'linear-gradient(180deg, rgba(255,255,255,0.03), transparent)' }} />
      <div style={{ position: 'absolute', left: edgeX, right: 0, top: lowerY + 2, height: 300, background: 'linear-gradient(180deg, rgba(255,255,255,0.03), transparent)' }} />
      <SideCane x={caneX} y={upperY} t={lt} walking={!alerted} />
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ position: 'absolute', left: 0, top: 0 }}>
        {beamOn && (
          <g>
            <line x1={emit.x} y1={emit.y} x2={hit.x} y2={hit.y}
              stroke={alerted ? RED : GREEN} strokeWidth={2.5} strokeOpacity={0.55} strokeDasharray="6 8" />
            <circle cx={hit.x} cy={hit.y} r={7 + Math.sin(lt * 9) * 2.5} fill={alerted ? RED : GREEN} opacity={0.9} />
            <Rings cx={hit.x} cy={hit.y} t={lt} startT={1.5} period={1.1} maxR={64}
              color={alerted ? RED : GREEN} alpha={0.55} n={2} />
            {/* alert rings on the grip (grip ≈ (caneX-138, upperY-608)) */}
            {alerted && <Rings cx={caneX - 138} cy={772} t={lt} startT={0} color={SOFT} maxR={130} />}
          </g>
        )}
      </svg>
      {beamOn && (
        <Chip x={540} y={840} o={env(lt, 1.6, 0.6, T.alert - T.down - 0.4, 0.4)}
          tone={alerted ? 'danger' : 'ok'}
          label={alerted ? 'Drop detected' : 'Surface OK'}
          value={alerted ? 'Stop' : 'Level'} />
      )}
      <Cap t0={T.down + 0.7} t1={T.down + 4.4} size={64} y={220} text="Reads the ground ahead." />
      <Cap t0={T.down + 5.6} t1={T.alert - 0.4} size={64} y={220} text="Kerbs. Stairs. Drop-offs." />
    </Frame>
  );
}

// ── Scene 6 · Alerts ────────────────────────────────────────────────
function Alerts() {
  const t = useTime();
  if (t < T.alert - 0.3 || t > T.outro + 0.2) return null;
  const lt = t - T.alert;
  const p = ((lt + 0.4) % 1.4);
  const buzz = Math.sin(lt * 88) * 4.5 * Math.exp(-p * 5) * CL((lt - 0.6) / 0.4, 0, 1);
  const pulse = Math.exp(-p * 5);
  const endLt = T.outro - T.alert - 0.4;
  return (
    <Frame t0={T.alert} t1={T.outro} zoomFrom={1.1} zoomTo={1.02} origin="50% 50%">
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(560px 560px at 50% 52%, rgba(59,130,246,0.11), transparent 70%)' }} />
      {/* Ring anchors below are the grip centre (492,542) and sensor-module
        * speaker area (560,1100) after this wrapper's rotate(-7°) scale(1.15)
        * about its 50% 40% origin. */}
      <div style={{ position: 'absolute', left: 445, top: 480, transform: `translateX(${buzz}px) rotate(-7deg) scale(1.15)`, transformOrigin: '50% 40%' }}>
        <Cane h={820} glow={0.35 + pulse * 0.5} />
      </div>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ position: 'absolute', left: 0, top: 0 }}>
        <Rings cx={492} cy={542} t={lt} startT={0.6} period={1.4} maxR={170} color={SOFT} alpha={0.7} />
        <Rings cx={560} cy={1100} t={lt} startT={1.0} period={1.4} maxR={210} color={ACCENT} alpha={0.55} n={4} />
      </svg>
      <MCallout lt={lt} at={1.6} endLt={endLt} y={215} title="Haptic pulse" sub="Felt in the grip" />
      <MCallout lt={lt} at={2.4} endLt={endLt} y={1400} title="Audio alert" sub="Clear, distinct beeps" />
      <Cap t0={T.alert + 3.4} t1={T.outro - 0.4} size={66} y={1620} text="Feel it. Hear it. Instantly." />
    </Frame>
  );
}

// ── Scene 7 · Outro ─────────────────────────────────────────────────
function Outro() {
  const t = useTime();
  if (t < T.outro - 0.3) return null;
  const lt = t - T.outro;
  return (
    <Frame t0={T.outro} t1={T.end - 0.05} zoomFrom={1.06} zoomTo={1} fadeOut={0.9}>
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(600px 560px at 50% 46%, rgba(59,130,246,0.08), transparent 70%)' }} />
      <Radar t={lt} alpha={0.28} cy={900} />
      <Pill t0={T.outro + 0.5} t1={T.end - 0.5} y={716} text="ClearWay" />
      <Cap t0={T.outro + 1.0} t1={T.end - 0.45} size={92} y={806} text="Smart Mobility." />
      <Cap t0={T.outro + 1.7} t1={T.end - 0.45} size={92} y={916} text="Real Confidence." />
      <Cap t0={T.outro + 2.8} t1={T.end - 0.45} size={22} y={1094} useGrad={false} color={DIM} weight={500} track="0.02em"
        text="ClearWay Mobility LLP" />
    </Frame>
  );
}

// ── Root ──────────────────────────────────────────────────────────
export default function ClearWayExplainerMobile({ time }) {
  return (
    <TimeContext.Provider value={time}>
      <div style={{ position: 'absolute', inset: 0 }}>
        <Intro />
        <Problem />
        <Reveal />
        <Forward />
        <Downward />
        <Alerts />
        <Outro />
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(105% 80% at 50% 46%, transparent 55%, rgba(0,0,0,0.55) 100%)' }} />
      </div>
    </TimeContext.Provider>
  );
}
